import {useMemo, useEffect} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import useBodyClass from '../../common/hooks/useBodyClassName';
import Button from '../../common/Button';
import { constants } from '../../common/constants/constants';
import arrowInCircleImg from '../../images/newDesign/arrow_in_white_circle.svg';
import bankImg from '../../images/newDesign/paymentMethods/bank.svg';
import bitImg from '../../images/newDesign/paymentMethods/bit.svg';
import cardImg from '../../images/newDesign/paymentMethods/creditCard.svg';
import chequeImg from '../../images/newDesign/paymentMethods/cheque.svg';
import cashImg from '../../images/newDesign/paymentMethods/cash.svg';
import applePayImg from '../../images/newDesign/paymentMethods/applePay.svg';
import PaymentFlowContainer from './PaymentFlowContainer';
import {useDispatch, useSelector} from "react-redux";
import {setPaymentMethod, setPaymentMethodId,setNumberOfPaymentMethods} from "../../store/tenant/slice";
import classNames from "classnames";
import {PaymentMethodName} from "../../common/constants/PaymentMethodName";

export function ChoosePaymentMethod({ match, address }) {
    const { t } = useTranslation();
    const { buildingID } = match.params;
    const history = useHistory();
    const dispatch = useDispatch();

    const creditCardDisabled = useSelector(state => state.tenant.buildingCreditCardDisabled ?? false);
    const bankChargeDisabled = useSelector(state => state.tenant.buildingBankChargeDisabled ?? false);
    // https://bllink.atlassian.net/jira/software/projects/SSB/boards/7?assignee=62dd3b4b3257defaf50c83b7&selectedIssue=SSB-850
    // default being reversed without reversing the name in order to save time on BE
    const chequePaymentsEnabled = useSelector(state => {
        return state.tenant.buildingChequePaymentsEnabled ?? false
    });
    const cashPaymentsEnabled = useSelector(state => state.tenant.buildingCashPaymentsEnabled ?? false);
    const numberOfPaymentMethods = useSelector(state => state.tenant.numberOfPaymentMethods);
    const tenantCommission = useSelector(state => state.tenant.tenantCommission);
    const bitPaymentsDisabled = useSelector(state => state.tenant.buildingBitPaymentsDisabled ?? false);
    //todoAriel switch the ! operator back, for testing only
    const applePayPaymentsDisabled = !useSelector(state => state.tenant.buildingMobilePaymentsDisabled ?? false);
    const currentBuildingData = useSelector(state => state.buildings.currentBuildingData);
    const externalPartnerId = currentBuildingData?.managementCompany?.externalId;

    useBodyClass('payment-flow');

    const paymentMethod = useSelector(state => state.tenant.paymentMethod);

    const allowedInternalBuildingIds = ['1904'];
    const allowedExternalPartnerIds = ['bllink', 'agas'];
    const isPrivatePartner = externalPartnerId === null;

    const isEntityAllowed = (
        allowedInternalBuildingIds.includes(buildingID) ||
        allowedExternalPartnerIds.includes(externalPartnerId) ||
        isPrivatePartner
    );

    const nextUrl = useMemo(() => {
        switch (paymentMethod) {
            case PaymentMethodName.bit:
            case PaymentMethodName.applePay:
            case PaymentMethodName.creditCards:
                return `/${constants.pages.tenantDetailsUrl}-new/${buildingID}`;
            case PaymentMethodName.bankCharge:
                return isEntityAllowed ? `/${constants.pages.bankPaymentInfo}/${buildingID}` : `/${constants.pages.tenantDetailsUrl}-new/${buildingID}`;
            case PaymentMethodName.cash:
            case PaymentMethodName.cheques:
                return `/${constants.pages.collectionDate}/${buildingID}`;
            default:
                throw new Error(`Unexpected payment type '${paymentMethod}'`);
        }
    }, [paymentMethod]);

    function handleContinueBtnClick() {
        history.push({ pathname: nextUrl });
    }

    function handleOptionSelect(method) {
        dispatch(setPaymentMethod(method));
        dispatch(setPaymentMethodId(constants.APIContract.paymentMethodsIds[method]));
    }

    function PaymentMethodItem({ method, image, label, disabled = false, tenantCommission }) {
        if (disabled) return null

        const classList = classNames('method-item', { 'selected': paymentMethod === method, 'disabled': disabled });
        const creditCardText = tenantCommission > 0 ? t('payments.comissionOn') : t('payments.noComission');
        return (
            <div onClick={() => handleOptionSelect(method)} className={classList}>
                <img src={image} />
                <div className='method-label'>
                    {label}
                    {method === PaymentMethodName.creditCards && <div className='credit-card-text'>{creditCardText}</div>}
                </div>
                <div className='radio-btn-outer'>
                    <div className='radio-btn-inner'></div>
                </div>
            </div>
        );
    }

    useEffect(() => {
        dispatch(setNumberOfPaymentMethods(document.querySelector('.methods')?.children.length ?? 0))
    }, [])

    return (
        <PaymentFlowContainer title={t('payments.payment_for')} subheader={t('payments.how_to_pay')} numOfMethods={numberOfPaymentMethods} showHeaderMessage>
            <div className='credit-card-and-actions-block managers choose-method'>
                <div className='methods'>
                    <PaymentMethodItem method={PaymentMethodName.creditCards} image={cardImg} label={t('payments.credit_card')} disabled={creditCardDisabled} tenantCommission={tenantCommission} />
                    <PaymentMethodItem method={PaymentMethodName.bankCharge} image={bankImg} label={t('payments.bank')} disabled={bankChargeDisabled}/>
                    <PaymentMethodItem method={PaymentMethodName.bit} image={bitImg} label={t('payments.bit')} disabled={bitPaymentsDisabled}/>
                    <PaymentMethodItem method={PaymentMethodName.applePay} image={applePayImg} label={t('payments.applePay')} disabled={applePayPaymentsDisabled}/>
                    <PaymentMethodItem method={PaymentMethodName.cheques} image={chequeImg} label={t('payments.cheque')} disabled={!chequePaymentsEnabled} />
                    <PaymentMethodItem method={PaymentMethodName.cash} image={cashImg} label={t('offline_payments.cash')} disabled={!cashPaymentsEnabled}/>
                </div>
                <Button onClick={handleContinueBtnClick}>{t('expenses.set_expenses.continue')} <img className='arrow' src={arrowInCircleImg} /></Button>
            </div>
        </PaymentFlowContainer>
    )
}
